/**
 * 세모 로고 로딩 모션.
 *
 * 새 심벌(피그마 «final logo», 2026-10-27)은 반투명 원을 사선으로 겹쳐 찍은 붓 자국 두 개다.
 * 원의 위치·크기·색은 원본 그대로이고, 어느 원이 얼마나 보이는지와 획 위치만 바뀐다.
 *
 *  - 화면 로딩: 원 두 개가 시차를 두고 각자 한 획씩, 위에서 아래로 빠르게 지나가며 그린다
 *    (위 획 먼저, 아래 획이 조금 늦게). 잠깐 완성된 로고를 보여 준 뒤 같은 방향으로 꼬리가 지워지고,
 *    그 사이 다음 원들이 다시 위에서 그려 내려온다. 끊기거나 비는 순간 없이 이어진다
 *    (예전 «원이 여러 모양을 그리며 이어지던» 로딩의 결).
 *  - 버튼: 완성된 두 획이 사선을 따라 엇갈리듯 벌어졌다가 다시 맞물리고, 맞물린 채로 오래 머문다.
 * 한 화면에 로딩이 여러 개 떠도 그러데이션 이름(id)이 겹치지 않게 로딩마다 접두어를 붙인다.
 */
import {
  SEMO_LOWER_STROKE,
  SEMO_MARK_HEIGHT,
  SEMO_MARK_WIDTH,
  SEMO_STROKE_DIR,
  SEMO_UPPER_STROKE,
  type BrushStroke,
} from "./semoMarkData";

/** 지나가는 원(붓 끝)의 자리와 진하기 */
export interface BrushHead {
  x: number;
  y: number;
  opacity: number;
}

/**
 * 한 장면의 상태.
 * reveal — 획(0 아래 · 1 위)의 i 번째 원이 얼마나 보이는지(0~1). 없으면 모두 보인다.
 * shift — 두 획이 사선을 따라 서로 멀어진 거리. heads — 획마다 지나가는 원([아래, 위]). opacity — 전체 불투명도.
 */
export interface BrushFrame {
  reveal?: (stroke: 0 | 1, i: number) => number;
  shift: number;
  opacity: number;
  heads?: readonly [BrushHead | null, BrushHead | null];
}

const DONE: BrushFrame = { shift: 0, opacity: 1 };

/** 심벌 둘레에 붓 자국 번짐이 잘리지 않을 만큼 여유를 둔 영역 */
const PAD = 4;
export const SEMO_MOTION_VIEWBOX = `${-SEMO_MARK_WIDTH / 2 - PAD} ${-SEMO_MARK_HEIGHT / 2 - PAD} ${SEMO_MARK_WIDTH + PAD * 2} ${SEMO_MARK_HEIGHT + PAD * 2}`;
/** 화면 로딩 상자의 가로:세로 — 심벌 비율 그대로 */
export const SEMO_MOTION_ASPECT =
  (SEMO_MARK_WIDTH + PAD * 2) / (SEMO_MARK_HEIGHT + PAD * 2);
/** 버튼 로딩 영역. 정사각형 안에 심벌을 세운다. */
export const SEMO_BUTTON_VIEWBOX = "-52 -52 104 104";

export type SemoButtonMotionKind = "symbol" | "text";

const clamp = (x: number) => Math.max(0, Math.min(1, x));
const mix = (a: number, b: number, p: number) => a + (b - a) * p;

function bezier(x1: number, y1: number, x2: number, y2: number) {
  const at = (t: number, a: number, b: number) =>
    3 * (1 - t) * (1 - t) * t * a + 3 * (1 - t) * t * t * b + t * t * t;
  return (x: number) => {
    x = clamp(x);
    if (x === 0 || x === 1) return x;
    let lo = 0;
    let hi = 1;
    for (let i = 0; i < 17; i++) {
      const mid = (lo + hi) / 2;
      if (at(mid, x1, x2) < x) lo = mid;
      else hi = mid;
    }
    return at((lo + hi) / 2, y1, y2);
  };
}

/** 빠르게 지나가되 끝에서 살짝 늦춘다 */
const sweep = bezier(0.3, 0, 0.2, 1);
/** 벌어졌다 맞물리기 */
const swing = bezier(0.45, 0, 0.2, 1);
const span = (t: number, from: number, to: number) =>
  clamp((t - from) / (to - from));

// ── 화면 로딩: 원 두 개가 시차를 두고 위에서 아래로 지나가며 그린다 ─────────────

/** 한 번 그리고 지우는 데 걸리는 시간(초). 다음 원들은 이 간격으로 출발한다 */
export const SEMO_DRAW_PERIOD = 1.2;
/** 원 하나가 제 획을 다 지나는 시간(초) */
const DRAW_TIME = 0.36;
/** 아래 획 원이 위 획 원보다 늦게 출발하는 시간(초) */
const DRAW_LAG = 0.16;
/** 완성된 로고를 보여 준 뒤 꼬리가 지워지기 시작하는 때(초). 지우기도 같은 빠르기·시차로 위에서 아래로 */
const ERASE_AT = 0.95;

const STROKES: readonly [BrushStroke, BrushStroke] = [
  SEMO_LOWER_STROKE,
  SEMO_UPPER_STROKE,
];
/** 획마다 출발이 늦는 시간 — [아래, 위] */
const STROKE_DELAY: readonly [number, number] = [DRAW_LAG, 0];

/** 이번 바퀴 시작 후 u 초에, 원이 제 획에서 몇 번째 원까지 왔는지(0~n). 위 끝에서부터 센다 */
function travelled(u: number, n: number): number {
  return sweep(span(u, 0, DRAW_TIME)) * n;
}

/** 지우기는 끝에서 늦추지 않고 점점 빨라진다 — 늦추면 획 끝의 원 몇 개가 점처럼 홀로 남는다 */
const erase = bezier(0.45, 0, 0.9, 0.6);
function erased(u: number, n: number): number {
  return erase(span(u, 0, DRAW_TIME)) * n;
}
/** 지우기가 이만큼(FROM) 지나면 남은 끝부분도 함께 흐려져 TO 에서 다 사라진다 — 점 하나 남지 않게 */
const ERASE_FADE_FROM = 0.35;
const ERASE_FADE_TO = 0.8;

function stampPoint(stroke: BrushStroke, i: number): { x: number; y: number } {
  const s = stroke.stamps[Math.max(0, Math.min(stroke.stamps.length - 1, i))];
  return { x: s?.[0] ?? 0, y: s?.[1] ?? 0 };
}

/** 앞머리(그리는 쪽)를 바퀴 수까지 더한 «끝없는 순서»로 센다. 아직 출발 전이면 -Infinity */
function frontAt(
  t: number,
  n: number,
  move: (u: number, n: number) => number = travelled,
): { at: number; cycle: number; u: number } {
  if (t < 0) return { at: -Infinity, cycle: -1, u: 0 };
  const cycle = Math.floor(t / SEMO_DRAW_PERIOD);
  const u = t - cycle * SEMO_DRAW_PERIOD;
  return { at: cycle * n + move(u, n), cycle, u };
}

function drawFrame(t: number): BrushFrame {
  // 획마다: 앞머리와 꼬리를 «끝없는 순서»로 세어, 꼬리가 지우는 동안 다음 원이 그리는 장면이 겹치게 한다
  const state = STROKES.map((stroke, k) => {
    const n = stroke.stamps.length;
    const local = t - (STROKE_DELAY[k] ?? 0);
    const front = frontAt(local, n);
    const tail = frontAt(local - ERASE_AT, n, erased);
    return { stroke, n, front, tail };
  });

  const reveal = (stroke: 0 | 1, i: number) => {
    const s = state[stroke];
    if (!s || s.front.cycle < 0) return 0;
    // 원본 데이터는 왼쪽 아래 → 오른쪽 위 순서라 위 끝에서부터 세도록 뒤집는다
    const k = s.n - 1 - i;
    let best = 0;
    for (const c of [s.front.cycle - 1, s.front.cycle]) {
      // 첫 바퀴 앞에는 지울 것이 없다 — 시작하자마자 빈 상자에서 그려 내려온다
      if (c < 0) continue;
      const g = c * s.n + k;
      // 지금 지워지는 바퀴라면, 지우기가 후반에 들면 남은 부분도 같이 흐려진다
      const fading =
        c === s.tail.cycle
          ? 1 -
            span((s.tail.at - c * s.n) / s.n, ERASE_FADE_FROM, ERASE_FADE_TO)
          : 1;
      best = Math.max(
        best,
        clamp(s.front.at - g) * clamp(g + 1 - s.tail.at) * fading,
      );
    }
    return best;
  };

  // 지나가는 원은 그리는 동안만 보인다. 들어올 때 빠르게 진해지고 다 그리면 사라진다
  const heads = state.map(({ stroke, n, front }) => {
    if (front.cycle < 0 || front.u >= DRAW_TIME) return null;
    const pos = travelled(front.u, n);
    const i = Math.floor(pos);
    const a = stampPoint(stroke, n - 1 - i);
    const b = stampPoint(stroke, n - 2 - i);
    const f = pos - i;
    return {
      x: mix(a.x, b.x, f),
      y: mix(a.y, b.y, f),
      opacity:
        0.55 *
        span(front.u, 0, 0.05) *
        (1 - span(front.u, DRAW_TIME - 0.08, DRAW_TIME)),
    };
  });
  return {
    reveal,
    shift: 0,
    opacity: 1,
    heads: [heads[0] ?? null, heads[1] ?? null],
  };
}

/** 화면 로딩 한 장면. t 는 시작 후 흐른 시간(초). 바퀴 사이가 끊기지 않으므로 그대로 넘기면 된다 */
export function screenFrame(t: number): BrushFrame {
  return drawFrame(t);
}

/** 버튼 로딩 한 바퀴(초): 벌어지기 0~0.28 · 잠깐 떨어져 있기 · 맞물리기 0.36~0.66 · 맞물린 채 0.74초 */
const BUTTON_PERIOD = 1.4;
/** 두 획이 벌어지는 거리(심벌 높이 100 기준) */
const SWING = 12;

/**
 * 버튼 로딩의 한 장면. t 는 시작 후 흐른 시간(초).
 * 두 종류 모두 «두 획이 벌어졌다 맞물리는» 움직임 하나로 통일했다(2026-10-28). kind 는 부르는 쪽 호환용.
 */
export function buttonFrame(
  _kind: SemoButtonMotionKind,
  t: number,
): BrushFrame {
  const u = t % BUTTON_PERIOD;
  const apart = swing(span(u, 0, 0.28)) - swing(span(u, 0.36, 0.66));
  return { shift: apart * SWING, opacity: 1 };
}

/** 움직임을 끈 사람(시스템 «동작 줄이기»)에게 보여 줄 정지 장면 — 완성된 심벌. */
export function stillFrame(): BrushFrame {
  return DONE;
}

const SVG_NS = "http://www.w3.org/2000/svg";
/** 원본 그러데이션 방향 — 원마다 30° 기울어진 축(피그마 원본의 좌표를 원 크기 비율로 옮긴 값) */
const GRADIENT_AXIS = { x1: "0.933", y1: "0.75", x2: "0.067", y2: "0.25" };
const GRADIENT_STOPS = ["0", "0.52", "1"] as const;
/** 원본이 원마다 걸어 둔 회전. 원 모양은 그대로이고 그러데이션 방향만 돌린다. */
const STAMP_ROTATE = 115.1;
/** 지나가는 원의 색 — 심벌 그러데이션의 가운데 보라 */
const HEAD_COLOR = "#8A28E0";

function el<K extends keyof SVGElementTagNameMap>(
  name: K,
  attrs: Record<string, string>,
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, name);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  return node;
}

interface PaintedStroke {
  group: SVGGElement;
  circles: SVGCircleElement[];
  base: number[];
  sign: 1 | -1;
}

/**
 * layer 안에 심벌의 원들을 한 번 만들어 두고, 장면마다 원의 불투명도와 획 위치만 바꾸는 붓을 돌려준다.
 * scale 은 심벌 높이 100 을 몇으로 그릴지, mono 를 주면 그 단색으로 칠한다(보라 버튼 위의 흰 심벌).
 */
export function createBrushPainter(
  layer: SVGGElement,
  prefix: string,
  options: { scale?: number; mono?: string } = {},
): (frame: BrushFrame) => void {
  const { scale = 1, mono } = options;
  while (layer.firstChild) layer.removeChild(layer.firstChild);
  const defs = el("defs", {});
  const root = el("g", { transform: `scale(${scale})` });
  layer.append(defs, root);

  let gradientIndex = 0;
  const build = (s: BrushStroke, sign: 1 | -1): PaintedStroke => {
    const group = el("g", {});
    root.append(group);
    const circles: SVGCircleElement[] = [];
    const base: number[] = [];
    for (const [cx, cy, opacity, c0, c1, c2] of s.stamps) {
      let fill = mono ?? "";
      if (!mono) {
        const id = `${prefix}g${gradientIndex++}`;
        const gradient = el("linearGradient", { id, ...GRADIENT_AXIS });
        [c0, c1, c2].forEach((color, i) =>
          gradient.append(
            el("stop", {
              offset: GRADIENT_STOPS[i] ?? "1",
              "stop-color": color,
            }),
          ),
        );
        defs.append(gradient);
        fill = `url(#${id})`;
      }
      const circle = el("circle", {
        cx: String(cx),
        cy: String(cy),
        r: String(s.r),
        fill,
        opacity: "0",
        transform: `rotate(${STAMP_ROTATE} ${cx} ${cy})`,
      });
      group.append(circle);
      circles.push(circle);
      base.push(opacity);
    }
    return { group, circles, base, sign };
  };
  // 겹치는 순서는 원본대로 — 아래 획을 뒤에, 위 획을 앞에
  const strokes = [build(SEMO_LOWER_STROKE, -1), build(SEMO_UPPER_STROKE, 1)];
  // 지나가는 원 — 획마다 하나씩, 그 획의 원과 같은 크기
  const heads = [SEMO_LOWER_STROKE, SEMO_UPPER_STROKE].map((s) => {
    const head = el("circle", {
      r: String(s.r),
      fill: mono ?? HEAD_COLOR,
      opacity: "0",
    });
    root.append(head);
    return head;
  });

  return (frame: BrushFrame) => {
    root.setAttribute("opacity", String(frame.opacity));
    strokes.forEach((s, k) => {
      const stroke = k === 0 ? 0 : 1;
      s.circles.forEach((circle, i) => {
        const shown = frame.reveal ? frame.reveal(stroke, i) : 1;
        circle.setAttribute("opacity", String((s.base[i] ?? 0) * shown));
      });
      const d = frame.shift * s.sign;
      s.group.setAttribute(
        "transform",
        `translate(${SEMO_STROKE_DIR.x * d} ${SEMO_STROKE_DIR.y * d})`,
      );
    });
    heads.forEach((head, k) => {
      const h = frame.heads?.[k];
      if (!h) {
        head.setAttribute("opacity", "0");
        return;
      }
      head.setAttribute("cx", String(h.x));
      head.setAttribute("cy", String(h.y));
      head.setAttribute("opacity", String(h.opacity));
    });
  };
}
